import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener, Input } from '@angular/core';
import { TasksByLpaService } from './tasks-by-lpa.service';
import { ChartData, FilterOptions, TasksByLpaData, TasksByLpaResponse, SeriesItem } from '../../models/tasks-by-lpa.models';
import { NgbCalendar } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-tasks-by-lpa',
    templateUrl: './tasks-by-lpa.component.html',
    styleUrls: ['./tasks-by-lpa.component.scss'],
    standalone: false
})
export class TasksByLpaComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  // Input property to control filter visibility from parent component
  @Input() isFilterVisible: boolean = false;

  // Chart options - optimized for better space usage
  view: any[] = [700, 350];
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = true;
  xAxisLabel = 'Task Count';
  showYAxisLabel = true;
  yAxisLabel = 'LPC';
  animations = true;
  legendPosition = 'bottom';
  
  // Fixed color scheme with explicit mapping for each category
  colorScheme = {
    domain: [
      '#FF0000',  // Red for Past Due (FIRST position)
      '#0000FF',  // Blue for Due > 30 Days (SECOND position)
      '#5AA454',  // Green for Completed
      '#FFA500',  // Orange for Due <= 7 Days
      '#FFFF00',  // Yellow for Due <= 15 Days
      '#008000'   // Green for Due <= 30 Days
    ]
  };
  
  // Define category colors map for consistent coloring
  categoryColorMap: { [key: string]: string } = {
    'Past Due': '#FF0000',       // Red
    'Due > 30 Days': '#0000FF',  // Blue
    'Completed': '#5AA454',      // Green
    'Due <= 7 Days': '#FFA500',  // Orange
    'Due <= 15 Days': '#FFFF00', // Yellow
    'Due <= 30 Days': '#008000'  // Green
  };

  // Chart data
  chartData: ChartData[] = [];
  originalData: TasksByLpaData[] = [];
  
  // Filter options
  organizationOptions: any[] = [];
  matterOptions: any[] = [];
  practiceAreaOptions: any[] = [];
  projectOptions: any[] = [];
  
  // Selected filter values
  selectedDateRange: any = null;
  dateRangeValue: Date[] = [];
  minDateValue: Date = new Date(2020, 0, 1);
  maxDateValue: Date = new Date(2050, 11, 31); // Allow dates far into the future
  selectedOrganization: string | null = null;
  selectedMatter: string | null = null;
  selectedPracticeArea: string | null = null;
  selectedProject: string | null = null;
  
  // Filter visibility - always true as it's controlled by parent through isFilterVisible
  showFilters: boolean = true;
  
  // UI state
  isLoading: boolean = false;
  error: string | null = null;
  
  constructor(
    private tasksByLpaService: TasksByLpaService,
    private calendar: NgbCalendar
  ) {}

  ngOnInit(): void {
    this.initDateRange();
    this.fetchFilterOptions();
    this.loadData();
  }
  
  ngAfterViewInit(): void {
    // Initialize chart size after view is initialized
    setTimeout(() => {
      this.updateChartSize();
    }, 100);
  }
  
  /**
   * Initialize default date range (today to today + 30 days)
   */
  initDateRange(): void {
    const today = new Date();
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    
    // Set default date range for p-calendar (today-30 to today)
    this.dateRangeValue = [defaultStartDate, today];
    
    // Set initial filter values with formatted dates
    this.selectedDateRange = {
      start: defaultStartDate,
      end: today
    };
  }
  
  /**
   * Fetch filter options from API
   */
  fetchFilterOptions(): void {
    this.isLoading = true;
    
    // Fetch organizations
    this.tasksByLpaService.getOrganizationNames()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.organizationOptions = response.data;
          }
        },
        error: (error: any) => {
          console.error('Error fetching organizations:', error);
        }
      });
    
    // Only fetch matters if organization is selected
    if (this.selectedOrganization) {
      this.fetchMattersByOrganization(this.selectedOrganization);
    } else {
      // Clear matters if no organization is selected
      this.matterOptions = [];
    }
    
    // Fetch practice areas
    this.tasksByLpaService.getPracticeAreas()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.practiceAreaOptions = response.data;
            console.log('Practice areas loaded:', this.practiceAreaOptions);
          } else {
            console.warn('No practice area data received');
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('Error loading practice areas:', error);
          this.isLoading = false;
        }
      });
    
    // Projects will be fetched when a matter is selected
    this.projectOptions = [];
  }
  
  /**
   * Load chart data from API
   */
  loadData(): void {
    this.isLoading = true;
    this.error = null;
    
    // Format dates for API request
    let startDate = '';
    let endDate = '';
    
    if (this.dateRangeValue && this.dateRangeValue.length === 2) {
      startDate = this.formatDate(this.dateRangeValue[0]);
      endDate = this.formatDate(this.dateRangeValue[1]);
    }
    
    // Prepare filter options
    const filters: FilterOptions = {
      startDate,
      endDate,
      organization: this.selectedOrganization || '',
      matter: this.selectedMatter || '',
      practiceArea: this.selectedPracticeArea || '',
      project: this.selectedProject || ''
    };
    
    this.tasksByLpaService.getTasksByLpaData(filters)
      .subscribe({
        next: (response: TasksByLpaResponse) => {
          if (response && response.data) {
            this.originalData = response.data;
            this.processChartData(response.data);
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading tasks by LPC data:', err);
          this.error = 'Failed to load tasks by LPC data';
          this.isLoading = false;
        }
      });
  }
  
  /**
   * Process API data into chart format
   */
  processChartData(data: TasksByLpaData[]): void {
    // Group data by LPA, task status, and due category
    const groupedData: Map<string, Map<string, number>> = new Map();
    
    data.forEach(item => {
      const userName = item.userName || 'Unknown';
      const taskCount = item.taskCount || 0;
      
      // Initialize user data if not exists
      if (!groupedData.has(userName)) {
        groupedData.set(userName, new Map());
      }
      
      const userData = groupedData.get(userName)!;
      
      // For completed tasks, just use 'Completed' category
      if (item.taskCategory === 'Completed') {
        const currentCount = userData.get('Completed') || 0;
        userData.set('Completed', currentCount + taskCount);
      } 
      // For not completed tasks, use the due date category
      else if (item.taskCategory === 'Not Completed') {
        // Use the due category from the API response
        const dueCategory = item.dueCategory || 'No Due Date';
        
        // Skip 'No Due Date' category as per requirements
        if (dueCategory !== 'No Due Date') {
          const currentCount = userData.get(dueCategory) || 0;
          userData.set(dueCategory, currentCount + taskCount);
        }
      }
    });
    
    // CRITICAL: Define the order of categories to match the color scheme domain order
    const categoryOrder = [
      'Past Due',         // Must be first to match red color
      'Due > 30 Days',    // Must be second to match blue color
      'Completed',        // Third position
      'Due <= 7 Days',    // Fourth position
      'Due <= 15 Days',   // Fifth position
      'Due <= 30 Days'    // Sixth position
    ];
    
    // Convert to chart format
    this.chartData = Array.from(groupedData.entries()).map(([userName, categoryCounts]) => {
      // Create series array with categories in the EXACT order to match color scheme
      const series: SeriesItem[] = [];
      
      // Add categories in the specific order that matches our color domain
      categoryOrder.forEach(category => {
        const value = categoryCounts.get(category) || 0;
        // Only add non-zero values
        if (value > 0) {
          series.push({
            name: category,
            value: value
          });
        }
      });
      
      // If no series, add a placeholder
      if (series.length === 0) {
        series.push({ name: 'No Tasks', value: 0 });
      }
      
      return {
        name: userName,
        series: series
      };
    });
    
    // Sort by user name
    this.chartData.sort((a, b) => a.name.localeCompare(b.name));
    
    console.log('Processed chart data with fixed ordering:', this.chartData);
    
    // Update chart size based on new data
    setTimeout(() => this.updateChartSize(), 0);
  }
  
  // No longer needed as we're using fixed ordering to match the color domain
  
  /**
   * Format date to MM/DD/YY
   */
  formatDate(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear().toString().substr(2); // Get last 2 digits of year
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${month}/${day}/${year}`;
  }
  
  /**
   * Handle date range selection
   */
  onDateRangeSelect(event: any): void {
    // Date range selection handling
  }
  
  /**
   * Handle date range close
   */
  onDateRangeClose(event: any): void {
    if (this.dateRangeValue && this.dateRangeValue.length === 2) {
      // Both dates are selected, ready to filter
    }
  }
  
  /**
   * Apply filters and reload data
   */
  applyFilters(): void {
    this.loadData();
  }
  
  /**
   * Clear all filters and reload data
   */
  clearFilters(): void {
    this.initDateRange();
    this.selectedOrganization = null;
    this.selectedMatter = null;
    this.selectedPracticeArea = null;
    this.selectedProject = null;
    this.projectOptions = [];
    this.loadData();
  }
  
  /**
   * Toggle filter visibility
   * Note: This method is now controlled by the parent component through isFilterVisible
   */
  toggleFilters(): void {
    // This method is now deprecated as filter visibility is controlled by the parent
    // through the isFilterVisible input property
    this.showFilters = true; // Always true as visibility is controlled by isFilterVisible
  }
  
  /**
   * Handle organization selection change
   */
  onOrganizationChange(event: any): void {
    console.log('Organization changed:', event);
    
    // Reset matter selection when organization changes
    this.selectedMatter = null;
    
    // Fetch matters for the selected organization
    if (this.selectedOrganization) {
      this.fetchMattersByOrganization(this.selectedOrganization);
    } else {
      // Clear matters if no organization is selected
      this.matterOptions = [];
    }
  }
  
  /**
   * Fetch matters based on selected organization
   */
  fetchMattersByOrganization(organization: string): void {
    this.isLoading = true;
    this.tasksByLpaService.getMatterNames(organization)
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.matterOptions = response.data;
            console.log(`Matters loaded for ${organization}:`, this.matterOptions);
          } else {
            this.matterOptions = [];
            console.warn(`No matters found for ${organization}`);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error(`Error loading matters for ${organization}:`, error);
          this.matterOptions = [];
          this.isLoading = false;
        }
      });
  }
  
  /**
   * Handle matter selection change
   */
  onMatterChange(event: any): void {
    console.log('Matter changed:', event);
    
    // Reset project selection when matter changes
    this.selectedProject = null;
    
    // Fetch projects for the selected matter
    if (this.selectedMatter) {
      this.fetchProjectsByMatter(this.selectedMatter);
    } else {
      // Clear projects if no matter is selected
      this.projectOptions = [];
    }
  }
  
  /**
   * Handle practice area selection change
   */
  onPracticeAreaChange(event: any): void {
    // Practice area change handling
  }
  
  /**
   * Handle project selection change
   */
  onProjectChange(event: any): void {
    // Project change handling
  }
  
  /**
   * Fetch projects based on selected matter
   */
  fetchProjectsByMatter(matter: string): void {
    this.isLoading = true;
    this.tasksByLpaService.getProjects(matter)
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.projectOptions = response.data;
            console.log(`Projects loaded for ${matter}:`, this.projectOptions);
          } else {
            this.projectOptions = [];
            console.warn(`No projects found for ${matter}`);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error(`Error loading projects for ${matter}:`, error);
          this.projectOptions = [];
          this.isLoading = false;
        }
      });
  }
  
  /**
   * Update chart size on window resize
   */
  @HostListener('window:resize')
  onResize(): void {
    this.updateChartSize();
  }
  
  /**
   * Update chart size based on container dimensions and data content
   */
  private updateChartSize(): void {
    if (this.chartContainer) {
      const container = this.chartContainer.nativeElement;
      const width = container.offsetWidth;
      
      // Calculate height based on number of data items
      // Each bar needs ~60-80px, plus space for axis labels and legend
      const itemCount = this.chartData.length || 1;
      const heightPerItem = 60;
      const baseHeight = 100; // For axis and legend
      const calculatedHeight = (itemCount * heightPerItem) + baseHeight;
      
      // Set reasonable min and max bounds
      const chartHeight = Math.max(280, Math.min(calculatedHeight, 500));
      
      this.view = [width, chartHeight];
    }
  }
}
